// Feature-flag plumbing.
//
// The point of this module: infrastructure sized for *future* gating, even
// though we gate almost nothing today.
//
//   - A named catalogue (`FEATURE`) with metadata per flag, so UI + admin +
//     analytics + error copy all read from one place.
//   - A layered `isFeatureOn` resolver that reads from (in priority order)
//     a localStorage override, a build-time env var, or the static default
//     on the catalogue entry. Each layer is opt-in and zero-config when
//     unused.
//   - A React hook `useFeature` for component surfaces; a plain sync
//     `isFeatureOn` for services / hooks / non-render code.
//   - A `requireFeature(flag)` helper that throws `FeatureDeniedError`
//     (ADR 0003) when off — useful inside command handlers and the
//     future mutation registry (ADR 0013).
//
// Deliberately NOT in this commit:
//   - Server-side `feature_flags` table or per-user overrides. Today all
//     flags resolve from build-time config + local overrides. Supabase
//     integration lands when we have a reason for per-user gating
//     (Stripe tiers in Tier 2.3).
//   - Posthog / LaunchDarkly integration. Same reason.
//   - Tier-based resolution (`FEATURE.X.tier === 'BUSINESS'` → check
//     user's team.plan_tier). The `tier` field on catalogue entries is
//     metadata only for now; the resolver ignores it. When billing lands,
//     extend `isFeatureOn` to consult the user's tier.
//
// See `docs/adr/0005-feature-flag-plumbing.md`.

import { useEffect, useState } from 'react'
import { FeatureDeniedError } from './errors'

// ─── Feature catalogue ───────────────────────────────────────────────────────
//
// To add a flag:
//   1. Add a new entry below. Pick a SCREAMING_SNAKE_CASE key.
//   2. Decide `defaultOn` — `true` for opt-in features users should
//      see by default, `false` for things that ship dark.
//   3. If this flag gates a mutation, throw `FeatureDeniedError` at the
//      entry point via `requireFeature(flag)`.
//
// To remove a flag: delete the entry. TypeScript will flag every stale
// `isFeatureOn('X')` / `useFeature('X')` call as an error (the `flag`
// param is typed as `FeatureFlag`).

type SubscriptionTier = 'FREE' | 'BASIC' | 'BUSINESS' | 'ENTERPRISE'

interface FeatureMeta {
  /** Stable key for this flag. Must match the catalogue entry's outer key. */
  readonly flag: string
  /** Display name for admin tooling and UI copy. */
  readonly name: string
  /** What the feature does. Shown in admin + upgrade modals. */
  readonly description: string
  /**
   * Required subscription tier for this flag to be on.
   *
   * Metadata only in this commit — the resolver doesn't consult this yet.
   * Populated now so when billing (Tier 2.3) lands, the gating wiring is
   * already present at every callsite.
   */
  readonly tier?: SubscriptionTier
  /** Display a "Beta" tag in admin / upgrade UI. */
  readonly beta?: boolean
  /**
   * Default resolution when no override is set. `true` means the feature
   * is on for everyone unless explicitly disabled; `false` means it ships
   * dark behind a manual opt-in.
   */
  readonly defaultOn: boolean
}

export const FEATURE = {
  /**
   * Self-serve account deletion. DORMANT — the UI is intentionally not
   * rendered anywhere (see ADR 0038) because the `delete_my_account`
   * RPC only clears public-schema data; an Edge Function that calls
   * `auth.admin.deleteUser` would be needed to fully delete the auth
   * row, and that function hasn't been written.
   *
   * The flag itself stays in the catalogue because features.test.ts
   * and polling.test.ts use it as a known `defaultOn: false` fixture.
   * Flipping it on via devtools does nothing today — AccountSettings
   * no longer reads the flag.
   *
   * Re-enablement path (ADR 0038 §"What completion requires"):
   *   1. Edge Function `delete-my-auth-user` that invokes
   *      `auth.admin.deleteUser` via service-role on callback from
   *      the existing ACCOUNT_DELETED event.
   *   2. UI re-added in AccountSettings behind this flag.
   *   3. Flag flipped to `defaultOn: true` once manual test confirms.
   */
  ACCOUNT_DELETION: {
    flag: 'ACCOUNT_DELETION',
    name: 'Account deletion',
    description: 'Let users hard-delete their own account from Settings.',
    beta: true,
    defaultOn: false,
  },

  /**
   * Sales Navigator parallel inbox (kondo-parity 1.9). Currently
   * exposed by presence of a Sales Nav account (sidebar gated on
   * `hasSalesNav`); this flag lets us additionally gate it on tier
   * once billing lands. `defaultOn: true` keeps behaviour open for
   * every user today — flipping to tier-based resolution (§7
   * decision 3) is a one-keyword change here without touching
   * consumers.
   *
   * Consumers: `useSyncCursor('salesnav_*')`, Shell's Sales Nav
   * branch, and the (future) Sales Nav poller.
   */
  SALES_NAV: {
    flag: 'SALES_NAV',
    name: 'Sales Navigator inbox',
    description: 'Parallel inbox + cursor for LinkedIn Sales Navigator.',
    // tier: 'BUSINESS',  // enable once tiers exist per §7 decision 3
    defaultOn: true,
  },

  /**
   * LinkedIn Recruiter parallel inbox (kondo-parity 1.10). Same shape
   * as SALES_NAV above — flipped on by default, ready to tier-gate
   * when billing lands. Consumers: `useSyncCursor('recruiter_*')`,
   * Shell's Recruiter filter branch, the (future) Recruiter poller.
   */
  RECRUITER: {
    flag: 'RECRUITER',
    name: 'LinkedIn Recruiter inbox',
    description: 'Parallel inbox for LinkedIn Recruiter.',
    // tier: 'ENTERPRISE',  // enable once tiers exist per §7 decision 3
    defaultOn: true,
  },

  // Future examples (keep commented until shipped, so typescript doesn't
  // permit calls against non-existent flags):
  //
  // WEBHOOKS: {
  //   flag: 'WEBHOOKS',
  //   name: 'Integration webhooks',
  //   description: 'Send conversation + contact events to external tools.',
  //   tier: 'BUSINESS',
  //   defaultOn: true,
  // },
  // MCP: {
  //   flag: 'MCP',
  //   name: 'MCP relay (AI agents)',
  //   description: 'Connect Claude/ChatGPT/Cursor to your Slash inbox.',
  //   tier: 'BUSINESS',
  //   defaultOn: true,
  // },
} as const satisfies Record<string, FeatureMeta>

export type FeatureFlag = keyof typeof FEATURE

// ─── Resolver ────────────────────────────────────────────────────────────────

const LOCAL_STORAGE_PREFIX = 'fwdslash:flag:'

/**
 * Is this feature enabled right now?
 *
 * Resolution order (first match wins):
 *   1. localStorage: `fwdslash:flag:<FLAG>` = "on" | "off"
 *      — for devtools / QA overrides per-device
 *   2. Build-time env: `VITE_FEATURE_<FLAG>` = "on" | "off"
 *      — for dev / staging builds wanting different defaults
 *   3. Static default: `FEATURE[flag].defaultOn`
 *
 * Synchronous — safe to call in render, in services, in non-React code.
 * When we add reactive sources (Supabase, Posthog), this stays sync at
 * the point of read — it consults a cached store updated out-of-band.
 */
export function isFeatureOn(flag: FeatureFlag): boolean {
  // 1. Per-device override
  try {
    const v = typeof localStorage !== 'undefined'
      ? localStorage.getItem(LOCAL_STORAGE_PREFIX + flag)
      : null
    if (v === 'on') return true
    if (v === 'off') return false
  } catch {
    // storage blocked (private mode, CSP) — fall through
  }

  // 2. Build-time env
  const envKey = `VITE_FEATURE_${flag}`
  try {
    const env = import.meta.env as Record<string, string | undefined>
    const envVal = env[envKey]
    if (envVal === 'on') return true
    if (envVal === 'off') return false
  } catch {
    // running in a context without import.meta.env (tests sometimes)
  }

  // 3. Static default
  return FEATURE[flag].defaultOn
}

/**
 * Throw `FeatureDeniedError` if the feature is not enabled. Use in
 * mutation-style entry points where "off" means "refuse to run."
 *
 *   export async function deleteAccount() {
 *     requireFeature('ACCOUNT_DELETION')
 *     // ...
 *   }
 */
export function requireFeature(flag: FeatureFlag): void {
  if (!isFeatureOn(flag)) {
    throw new FeatureDeniedError(
      `Feature "${FEATURE[flag].name}" is not enabled for your account.`,
    )
  }
}

// ─── React hook ──────────────────────────────────────────────────────────────

/**
 * Subscribe a component to a feature flag. Re-renders when the flag flips
 * via a localStorage change in another tab (covers devtools QA on a second
 * tab and post-1.0 cross-tab sync).
 *
 * For non-render / service code, prefer `isFeatureOn` — it avoids the
 * hook subscription overhead and works outside React.
 */
export function useFeature(flag: FeatureFlag): boolean {
  const [on, setOn] = useState(() => isFeatureOn(flag))

  useEffect(() => {
    const key = LOCAL_STORAGE_PREFIX + flag

    // Cross-tab: StorageEvent fires for writes in *other* tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === key || e.key === null) setOn(isFeatureOn(flag))
    }
    window.addEventListener('storage', onStorage)

    // Same-tab: StorageEvent does NOT fire for writes in the same tab.
    // A custom event lets future dev-panel code toggle a flag and have
    // components re-render without a reload.
    const onLocalFlip = (e: Event) => {
      if (!(e instanceof CustomEvent)) return
      if (e.detail?.flag === flag) setOn(isFeatureOn(flag))
    }
    window.addEventListener('fwdslash:flag-changed', onLocalFlip)

    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('fwdslash:flag-changed', onLocalFlip)
    }
  }, [flag])

  return on
}

// ─── Dev-only helpers ────────────────────────────────────────────────────────

/**
 * Set a flag's per-device override. Fires a `fwdslash:flag-changed` event
 * so same-tab subscribers (`useFeature`) re-render immediately.
 *
 * Intended for dev tools / admin UI. Not wired into any user-facing flow.
 */
export function setFeatureOverride(flag: FeatureFlag, value: 'on' | 'off' | null): void {
  const key = LOCAL_STORAGE_PREFIX + flag
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // storage blocked — no-op
  }
  try {
    window.dispatchEvent(new CustomEvent('fwdslash:flag-changed', { detail: { flag, value } }))
  } catch {
    // no window (SSR / worker) — no-op
  }
}

/** Read the current override value without consulting env / defaults. */
export function getFeatureOverride(flag: FeatureFlag): 'on' | 'off' | null {
  try {
    const v = localStorage.getItem(LOCAL_STORAGE_PREFIX + flag)
    if (v === 'on' || v === 'off') return v
  } catch {}
  return null
}
